import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Wordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { homeFor } from "@/components/ProtectedRoute";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mfaToken ? await verifyMfa(mfaToken, code) : await login(email, password);
      if (u.mfaToken) {
        setMfaToken(u.mfaToken);
        return;
      }
      toast.success("Welcome to BHOLEBOOK");
      if (u.must_change_password) nav("/change-password");
      else if (u.mfa_setup_required) nav("/security");
      else nav(homeFor(u.role));
    } catch (err) {
      toast.error(formatError(err));
      if (err?.response?.status === 401 && mfaToken && /session|token/i.test(formatError(err))) setMfaToken(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(9,9,13,0.3), rgba(9,9,13,0.9)), url(https://images.pexels.com/photos/36741130/pexels-photo-36741130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-10 left-10 right-10">
          <div className="font-display text-5xl font-black gold-text">BHOLEBOOK</div>
          <div className="mt-2 font-display uppercase tracking-widest text-amber-300 text-lg">24x7 India's Best Book</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#09090D]">
        <Card className="w-full max-w-md bg-zinc-950 border-amber-500/20 gold-glow">
          <CardContent className="p-8">
            <Wordmark className="mb-8" />
            <h1 className="font-display text-3xl font-black uppercase mb-6">Login</h1>
            {mfaToken ? (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="mfa-form">
                <Label className="font-display uppercase tracking-widest text-xs text-amber-400">Authenticator code</Label>
                <Input
                  inputMode="numeric"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="login-mfa-code"
                  className="bg-black border-amber-500/20 text-center tracking-[0.5em] font-mono-stat text-lg"
                />
                <Button
                  disabled={busy || code.length !== 6}
                  data-testid="login-mfa-submit"
                  className="w-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest"
                >
                  {busy ? "Verifying…" : "Verify"}
                </Button>
                <button type="button" onClick={() => { setMfaToken(null); setCode(""); }} className="w-full text-xs text-zinc-500 hover:text-amber-300" data-testid="login-mfa-back">
                  Back
                </button>
              </form>
            ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <Label className="font-display uppercase tracking-widest text-xs text-amber-400">Email</Label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="bg-black border-amber-500/20 mt-1.5"
                />
              </div>
              <div>
                <Label className="font-display uppercase tracking-widest text-xs text-amber-400">Password</Label>
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="bg-black border-amber-500/20 mt-1.5"
                />
              </div>
              <Button
                disabled={busy}
                data-testid="login-submit"
                className="w-full bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest hover:brightness-110"
              >
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
